// https://docs.launchdarkly.com/sdk/client-side/react
import { useFlags as _useFlags, type LDClient, type LDContext } from 'launchdarkly-react-client-sdk';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { version } from '../../package.json';
import type { RawGeoData } from '../features/geography/types';
import { getErrorMessage } from '../utils/errors';
import { getCookie, setSecureCookie, removeSecureCookie } from '../utils/secureCookies';

import { getBrowserSessionId } from './analytics/getBrowserSessionId';
import type { Profile } from './auth';
import { PlatformEnum } from './platformContext/platformContextTypes';
import type { LDFlags } from './types';

export let ldClient: LDClient | null = null;
const LD_CONTEXT_COOKIE_NAME = 'ldContext';

export function getLDContextFromCookie(): LDContext | null {
  try {
    const cookieValue = getCookie(LD_CONTEXT_COOKIE_NAME);

    if (!cookieValue) {
      return null;
    }

    const context = JSON.parse(cookieValue) as LDContext;
    return context;
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to read LD context from cookie: ${errorMessage}`);
    return null;
  }
}

function computeCookieContent(
  ldContext: LDContext,
  cookieContext: LDContext | null,
  userStats?: Record<string, unknown>,
  frontendAttributesForLD?: string[] | null,
): LDContext | null {
  // Anonymous users: use ldContext as-is
  if (ldContext.anonymous === true) {
    return ldContext;
  }

  // Authenticated users with stats: merge ldContext with filtered userStats by frontendAttributesForLD
  if (userStats && frontendAttributesForLD) {
    const filteredUserStats = Object.fromEntries(
      Object.entries(userStats).filter(([key]) => frontendAttributesForLD.includes(key)),
    );
    return { ...ldContext, ...filteredUserStats };
  }

  // If cookie exists, only update existing cookie keys with values from ldContext
  // This prevents large ldContext from overwriting the cookie with unwanted fields
  if (cookieContext) {
    const updatedCookie: LDContext = { ...cookieContext };
    for (const key of Object.keys(cookieContext)) {
      const ldValue = ldContext[key as keyof LDContext];
      if (ldValue !== undefined) {
        (updatedCookie as Record<string, unknown>)[key] = ldValue;
      }
    }
    return updatedCookie;
  }

  // No existing cookie and no userStats: don't save anything
  return null;
}

export function saveLDContextToCookie(
  ldContext: LDContext,
  userStats?: Record<string, unknown>,
  ldAttributes?: string[] | null,
): void {
  try {
    const cookieContext = getLDContextFromCookie();
    const newCookieContent = computeCookieContent(ldContext, cookieContext, userStats, ldAttributes);

    if (!newCookieContent) {
      return;
    }

    if (cookieContext && isEqual(cookieContext, newCookieContent)) {
      return;
    }

    // Prevent cookie size from exceeding 3KB
    const newCookieContentStringify = JSON.stringify(newCookieContent);
    const MAX_COOKIE_SIZE_BYTES = 3 * 1024; // 3KB limit for cookie safety
    if (new Blob([newCookieContentStringify]).size > MAX_COOKIE_SIZE_BYTES) {
      return;
    }

    setSecureCookie(LD_CONTEXT_COOKIE_NAME, newCookieContentStringify, { expires: 1 });
  } catch (err) {
    removeSecureCookie(LD_CONTEXT_COOKIE_NAME);
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to save LD context to cookie: ${errorMessage}`);
  }
}

export function getBootstrapFlags(): Record<string, any> | undefined {
  try {
    if (typeof window !== 'undefined' && window.ldFlags) {
      return window.ldFlags;
    }
    return undefined;
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to read bootstrap flags: ${errorMessage}`);
    return undefined;
  }
}

function decodeLdAttributes(ldAttributes: string | null | undefined): string[] | null {
  try {
    if (!ldAttributes) {
      return null;
    }
    return JSON.parse(atob(ldAttributes));
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to decode LD attributes: ${errorMessage}`);
    return null;
  }
}

const onClientReady = () => {
  console.log('LaunchDarkly client is ready');
};

const onClientError = (err: Error) => {
  const errorMessage = err.message || err.toString();
  console.error(`LaunchDarkly error: ${errorMessage}`);
};

const onClientFailed = (err: Error) => {
  const errorMessage = err.message || err.toString();
  console.error(`LaunchDarkly failed: ${errorMessage}`);
};

const onClientChange = (changes: any) => {
  console.log(`LaunchDarkly flags changed after identify: ${JSON.stringify(changes)}`);
};

export function setLDClient(client: LDClient) {
  if (ldClient) {
    ldClient.off('ready', onClientReady);
    ldClient.off('error', onClientError);
    ldClient.off('failed', onClientFailed);
  }

  ldClient = client;

  ldClient.on('ready', onClientReady);
  ldClient.on('error', onClientError);
  ldClient.on('failed', onClientFailed);
  ldClient.on('change', onClientChange);
}

export async function identifyUser(ldContext: LDContext, attempt = 0) {
  const maxAttempts = 3;
  return new Promise((resolve) => {
    if (ldClient) {
      ldClient
        .identify(ldContext)
        .then(() => {
          console.log(`identify user success after attempt: ${attempt}`);
          saveLDContextToCookie(ldContext);
          resolve('success');
        })
        .catch((err: Error) => {
          const errorMessage = getErrorMessage(err);
          console.error(`LaunchDarkly identify error: ${errorMessage}, attempt ${attempt}`);
          if (attempt < maxAttempts) {
            console.warn(`Retrying identifyUser, attempt ${attempt + 1}`);
            setTimeout(
              () => {
                void identifyUser(ldContext, attempt + 1);
              },
              1000 * (attempt + 1),
            );
          } else {
            console.error(`Failed to identify user after ${maxAttempts} attempts`);
            resolve('failed');
          }
        });
    }
  });
}

export const getBaseLDContext = (): LDContext => {
  const sessionId = getBrowserSessionId();

  const baseContext: LDContext = {
    kind: 'user',
    session_id: sessionId,
    app_version: version,
    app_platform: PlatformEnum.WEB,
  };

  return baseContext;
};

export function getUserStats(stats: Record<string, any> | string | undefined): Record<string, any> | undefined {
  if (typeof stats === 'object') {
    return stats;
  }

  if (typeof stats === 'string') {
    try {
      const userStats = JSON.parse(decodeURIComponent(window.atob(stats)));
      return {
        ...getBaseLDContext(),
        ...userStats,
      };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error(`Failed to parse user stats: ${errorMessage}`);
    }
  }

  return;
}

export function createLDContext(profile?: Profile | null, rawGeoData?: RawGeoData | null): LDContext {
  const { firebaseUid, email, stats, language, ldAttr: ldAttributesEncoded, territory } = profile ?? {};

  const baseContext: LDContext = {
    country_code: territory?.countryCode,
    ip_country: rawGeoData?.country,
    ...getBaseLDContext(),
    ...(profile && firebaseUid
      ? {
          key: firebaseUid,
          email,
          language,
        }
      : {
          anonymous: true,
          key: 'PLAYER_FRONTEND_ANONYMOUS',
        }),
  };

  const userStats = getUserStats(stats);
  const ldContext: LDContext = firebaseUid ? { ...baseContext, ...userStats } : baseContext;
  const ldttributes = decodeLdAttributes(ldAttributesEncoded);

  saveLDContextToCookie(baseContext, userStats, ldttributes);

  return ldContext;
}

/**
 * Returns the static value of a flag
 * @param flag
 * @param defaultValue
 */
export function resolveFlag<T = boolean>(flag: LDFlags, defaultValue: T): T {
  if (ldClient) {
    return ldClient.variation(flag, defaultValue);
  }

  console.error(':resolveFlag: LaunchDarkly client is missing', { customAttributes: { flag, defaultValue } });

  if (window.newrelic) {
    window.newrelic.log('LaunchDarkly client is missing error', {
      level: 'error',
      customAttributes: {
        flag,
        defaultValue,
      },
    });

    window.newrelic.noticeError(new Error('LaunchDarkly client is missing'), {
      flag,
      defaultValue,
    });
  }

  return defaultValue;
}

/**
 * Returns the observable value of a flag
 * @param flag
 * @param defaultValue
 */
export function useFlag<T = boolean>(flag: LDFlags, defaultValue: T): T {
  const flags = _useFlags<Record<LDFlags, any>>();

  if (!isNil(flags[flag])) {
    return flags[flag];
  }

  return resolveFlag(flag, defaultValue);
}

/**
 * Wrapper for launch darkly useFlags hook
 */
export function useFlags(): Record<LDFlags, any> {
  return _useFlags<Record<LDFlags, any>>();
}
